import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, MapPin } from "lucide-react";
import type { Category } from "@/types/Category";

type MapFiltersProps = {
  status: string;
  setStatus: (value: string) => void;
  categoryId: string;
  setCategoryId: (value: string) => void;
  priority: string;
  setPriority: (value: string) => void;
  categories: Category[];
  categoryStats: Record<string, number>;
};

const statusColors: Record<string, string> = {
  AWAITING_ANALYSIS: "#eab308",
  IN_PROGRESS: "#3b82f6",
  RESOLVED: "#22c55e",
  REJECTED: "#ef4444",
  CANCELLED: "#6b7280",
  REOPENED: "#f97316",
  RETURNED: "#a855f7",
  AWAITING_REANALYSIS: "#ca8a04",
  FILED: "#4b5563",
};

const priorityColors: Record<string, string> = {
  LOW: "#22c55e",
  MEDIUM: "#eab308",
  HIGH: "#f97316",
  URGENT: "#ef4444",
};

export const MapFilters = ({
  status,
  setStatus,
  categoryId,
  setCategoryId,
  priority,
  setPriority,
  categories,
  categoryStats,
}: MapFiltersProps) => {
  return (
    <Card className="h-[600px] overflow-y-auto bg-white">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Filter className="h-4 w-4" />
          Filtros
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs font-medium mb-1.5 block">Status</label>
          <Select
            value={status || "all"}
            onValueChange={(v) => setStatus(v === "all" ? "" : v)}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="AWAITING_ANALYSIS">
                Aguardando Análise
              </SelectItem>
              <SelectItem value="IN_PROGRESS">Em Andamento</SelectItem>
              <SelectItem value="RESOLVED">Resolvida</SelectItem>
              {/* <SelectItem value="REJECTED">Rejeitada</SelectItem> */}
              <SelectItem value="CANCELLED">Cancelada</SelectItem>
              <SelectItem value="REOPENED">Reaberta</SelectItem>
              <SelectItem value="RETURNED">Devolvida</SelectItem>
              <SelectItem value="AWAITING_REANALYSIS">
                Aguardando Reanálise
              </SelectItem>
              <SelectItem value="FILED">Arquivada</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium mb-1.5 block">Categoria</label>
          <Select
            value={categoryId || "all"}
            onValueChange={(v) => setCategoryId(v === "all" ? "" : v)}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {Array.isArray(categories) &&
                categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Prioridade</label>
          <Select
            value={priority || "all"}
            onValueChange={(v) => setPriority(v === "all" ? "" : v)}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="LOW">Baixa</SelectItem>
              <SelectItem value="MEDIUM">Média</SelectItem>
              <SelectItem value="HIGH">Alta</SelectItem>
              <SelectItem value="URGENT">Crítica</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="pt-3 border-t">
          <h4 className="text-xs font-medium mb-2">Legenda de Status</h4>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: statusColors.AWAITING_ANALYSIS }}
              />
              <span className="text-xs text-muted-foreground">
                Aguardando Análise
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: statusColors.IN_PROGRESS }}
              />
              <span className="text-xs text-muted-foreground">
                Em Andamento
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: statusColors.RESOLVED }}
              />
              <span className="text-xs text-muted-foreground">Resolvida</span>
            </div>
            {/* <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: statusColors.REJECTED }}
              />
              <span className="text-xs text-muted-foreground">Rejeitada</span>
            </div> */}
          </div>
        </div>

        <div className="pt-3 border-t">
          <h4 className="text-xs font-medium mb-2">Legenda de Prioridade</h4>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: priorityColors.LOW }}
              />
              <span className="text-xs text-muted-foreground">Baixa</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: priorityColors.MEDIUM }}
              />
              <span className="text-xs text-muted-foreground">Média</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: priorityColors.HIGH }}
              />
              <span className="text-xs text-muted-foreground">Alta</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: priorityColors.URGENT }}
              />
              <span className="text-xs text-muted-foreground">Crítica</span>
            </div>
          </div>
        </div>

        {Object.keys(categoryStats).length > 0 && (
          <div className="pt-3 border-t">
            <h4 className="text-xs font-medium mb-2">Categorias</h4>
            <div className="space-y-1.5">
              {Object.entries(categoryStats)
                .slice(0, 5)
                .map(([catName, count]) => (
                  <div key={catName} className="flex items-center gap-2">
                    <MapPin className="h-3 w-3 text-primary" />
                    <span className="text-xs text-muted-foreground">
                      {catName}
                    </span>
                    <Badge variant="secondary" className="ml-auto">
                      {count}
                    </Badge>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
